import {
  IonButton,
  IonButtons,
  IonAlert,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonModal,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import { add } from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/client.js";
import { fetchClients } from "../api/clients.js";
import { fetchUsers } from "../api/users.js";
import { useAuth } from "../auth/AuthContext.jsx";
import DateRangeNavigator from "../components/DateRangeNavigator.jsx";
import AddressAutocompleteField from "../components/AddressAutocompleteField.jsx";
import FormNotice from "../components/FormNotice.jsx";
import SearchSelect from "../components/SearchSelect.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import { currentWeekRange, dateEnd, dateStart, elapsedSeconds, formatDate, formatHoursMinutes, formatTime } from "../utils/date.js";
import {
  calculatedScheduledEnd,
  durationPartsFromMinutes,
  estimatedMinutesFromParts,
  normalizeMinutePart,
} from "../utils/jobDuration.js";

const blank = {
  clientId: "",
  serviceAddress: "",
  internalLabel: "",
  scheduledStart: "",
  estimatedDurationHours: "",
  estimatedDurationMinutesPart: "00",
  serviceNotes: "",
  status: "Scheduled",
  assignedUserIds: [],
  isRepeated: false,
  repeatIntervalWeeks: 1,
};

function datetimeLocalValue(value) {
  const date = new Date(value);
  const timezoneOffsetMs = date.getTimezoneOffset() * 60000;

  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
}

function normalizeDateTimeLocalValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const localMatch = value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/);

    if (localMatch) {
      return localMatch[0];
    }
  }

  return datetimeLocalValue(value);
}

function repeatValidationMessage(currentForm) {
  if (!currentForm.isRepeated) {
    return "";
  }

  const scheduledStartDate = currentForm.scheduledStart ? currentForm.scheduledStart.slice(0, 10) : "";
  const intervalWeeks = Number(currentForm.repeatIntervalWeeks);

  if (!scheduledStartDate) {
    return "Scheduled start is required before repeating a job.";
  }

  if (!Number.isInteger(intervalWeeks) || intervalWeeks < 1 || intervalWeeks > 52) {
    return "Interval in weeks must be between 1 and 52.";
  }

  return "";
}

function repeatDetails(job) {
  if (!job.repeatGroupId) {
    return "";
  }

  const interval = job.repeatIntervalWeeks || 1;
  const unit = interval === 1 ? "week" : "weeks";

  if (job.lifecycleStatus === "Paused") {
    return `Paused · repeats every ${interval} ${unit}`;
  }

  if (job.lifecycleStatus === "Ended") {
    return `Ended · repeated every ${interval} ${unit}`;
  }

  return `Repeats every ${interval} ${unit}`;
}

function clientLabel(client) {
  return client?.name;
}

function jobLabel(job) {
  return job?.internalLabel ? `${job.client?.name} - ${job.internalLabel}` : job?.client?.name;
}

function primaryOccurrence(job) {
  const occurrences = job.scheduleOccurrences || [];
  const now = Date.now();

  return occurrences.find((occurrence) => new Date(occurrence.scheduledStart).getTime() >= now) || occurrences[0] || job;
}

function trackedSecondsForOccurrence(job, occurrence) {
  const fallbackSeconds = (() => {
    if (!occurrence?.scheduledStart || !occurrence?.scheduledEnd) {
      return (job.estimatedDuration || 0) * 60;
    }

    const scheduledSeconds = Math.max(
      0,
      Math.round((new Date(occurrence.scheduledEnd).getTime() - new Date(occurrence.scheduledStart).getTime()) / 1000),
    );
    const assignedCount = Math.max(1, occurrence.assignments?.length || job.assignments?.length || 0);

    return scheduledSeconds * assignedCount;
  })();

  if (!occurrence?.id) {
    const manualEntries = job.timeEntries?.filter((entry) => entry.source?.name === "Manual") || [];

    if (manualEntries.length > 0) {
      return manualEntries.reduce((sum, entry) => sum + elapsedSeconds(entry), 0);
    }

    const estimatedEntries = job.timeEntries?.filter((entry) => entry.source?.name === "Estimated") || [];

    if (estimatedEntries.length > 0) {
      return estimatedEntries.reduce((sum, entry) => sum + elapsedSeconds(entry), 0);
    }

    return fallbackSeconds;
  }

  const occurrenceEntries = job.timeEntries?.filter((entry) => entry.scheduleOccurrenceId === occurrence.id) || [];
  const manualEntries = occurrenceEntries.filter((entry) => entry.source?.name === "Manual");

  if (manualEntries.length > 0) {
    return manualEntries.reduce((sum, entry) => sum + elapsedSeconds(entry), 0);
  }

  const estimatedEntries = occurrenceEntries.filter((entry) => entry.source?.name === "Estimated");

  if (estimatedEntries.length > 0) {
    return estimatedEntries.reduce((sum, entry) => sum + elapsedSeconds(entry), 0);
  }

  return fallbackSeconds;
}

export default function JobsPage() {
  const { session } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(blank);
  const [editForm, setEditForm] = useState(blank);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [pendingLifecycleAction, setPendingLifecycleAction] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Scheduled");
  const [dateFilters, setDateFilters] = useState(currentWeekRange);
  const [toast, setToast] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [isUpdatingJob, setIsUpdatingJob] = useState(false);
  const estimatedDurationMinutes = useMemo(
    () => estimatedMinutesFromParts(form.estimatedDurationHours, form.estimatedDurationMinutesPart),
    [form.estimatedDurationHours, form.estimatedDurationMinutesPart],
  );
  const editEstimatedDurationMinutes = useMemo(
    () => estimatedMinutesFromParts(editForm.estimatedDurationHours, editForm.estimatedDurationMinutesPart),
    [editForm.estimatedDurationHours, editForm.estimatedDurationMinutesPart],
  );
  const repeatError = repeatValidationMessage(form);
  const editRepeatError = repeatValidationMessage(editForm);
  const createFormNotice = repeatError;
  const editFormNotice = editRepeatError;

  const query = useMemo(() => {
    const params = new URLSearchParams();

    if (search) {
      params.set("search", search);
    }

    if (status) {
      params.set("status", status);
    }

    params.set("from", dateStart(dateFilters.startDate).toISOString());
    params.set("to", dateEnd(dateFilters.endDate).toISOString());

    return params.toString() ? `?${params}` : "";
  }, [dateFilters, search, status]);

  const load = useCallback(async () => {
    const [jobData, clientData, userData] = await Promise.all([
      apiFetch(`/jobs${query}`),
      fetchClients(),
      fetchUsers(),
    ]);

    setJobs(jobData);
    setClients(clientData);
    setUsers(userData);
  }, [query]);

  useEffect(() => {
    load().catch((err) => setToast(err.message));
  }, [load]);

  useIonViewWillEnter(() => {
    setDateFilters(currentWeekRange());
  });

  async function createJob() {
    if (!form.scheduledStart) {
      setToast("Scheduled start is required.");

      return;
    }

    if (!estimatedDurationMinutes) {
      setToast("Estimated duration must be greater than zero.");

      return;
    }

    if (repeatError) {
      setToast(repeatError);

      return;
    }

    if (form.isRepeated && !form.repeatIntervalWeeks) {
      setToast("Interval in weeks must be between 1 and 52.");

      return;
    }

    try {
      setIsCreatingJob(true);
      const scheduledEnd = calculatedScheduledEnd(
        form.scheduledStart,
        estimatedDurationMinutes,
        form.assignedUserIds.length,
      );

      await apiFetch("/jobs", {
        method: "POST",
        body: {
          ...form,
          internalLabel: form.internalLabel.trim() || undefined,
          scheduledStart: new Date(form.scheduledStart),
          scheduledEnd,
          estimatedDuration: estimatedDurationMinutes,
          repeatIntervalWeeks: form.isRepeated ? Number(form.repeatIntervalWeeks) : undefined,
        },
      });

      setForm(blank);
      setIsCreateOpen(false);
      await load();
      setToast("Job created.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsCreatingJob(false);
    }
  }

  async function openCreateJob() {
    try {
      const [clientData, userData] = await Promise.all([fetchClients(), fetchUsers()]);

      setClients(clientData);
      setUsers(userData);
      setIsCreateOpen(true);
    } catch (err) {
      setToast(err.message);
    }
  }

  async function updateJob() {
    if (!editForm.scheduledStart) {
      setToast("Scheduled start is required.");

      return;
    }

    if (!editEstimatedDurationMinutes) {
      setToast("Estimated duration must be greater than zero.");

      return;
    }

    if (editRepeatError) {
      setToast(editRepeatError);

      return;
    }

    if (editForm.isRepeated && !editForm.repeatIntervalWeeks) {
      setToast("Interval in weeks must be between 1 and 52.");

      return;
    }

    try {
      setIsUpdatingJob(true);
      const scheduledEnd = calculatedScheduledEnd(
        editForm.scheduledStart,
        editEstimatedDurationMinutes,
        editForm.assignedUserIds.length,
      );

      await apiFetch(`/jobs/${selectedJob.id}`, {
        method: "PUT",
        body: {
          ...editForm,
          internalLabel: editForm.internalLabel.trim() || undefined,
          scheduledStart: new Date(editForm.scheduledStart),
          scheduledEnd,
          estimatedDuration: editEstimatedDurationMinutes,
          repeatIntervalWeeks: editForm.isRepeated ? Number(editForm.repeatIntervalWeeks) : undefined,
        },
      });

      setSelectedJob(null);
      setEditForm(blank);
      await load();
      setToast("Job updated.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsUpdatingJob(false);
    }
  }

  function updateScheduledStart(value, currentForm, setCurrentForm) {
    const scheduledStart = normalizeDateTimeLocalValue(value);
    const nextForm = { ...currentForm, scheduledStart };
    setCurrentForm(nextForm);
  }

  function openEditJob(job) {
    const durationParts = durationPartsFromMinutes(job.estimatedDuration);

    setSelectedJob(job);
    setEditForm({
      clientId: job.clientId,
      serviceAddress: job.serviceAddress,
      internalLabel: job.internalLabel ?? "",
      scheduledStart: datetimeLocalValue(job.scheduledStart),
      estimatedDurationHours: durationParts.hours,
      estimatedDurationMinutesPart: durationParts.minutes,
      serviceNotes: job.serviceNotes ?? "",
      status: job.status,
      assignedUserIds: job.assignments?.map((assignment) => assignment.userId) ?? [],
      isRepeated: Boolean(job.repeatGroupId),
      repeatIntervalWeeks: job.repeatIntervalWeeks || 1,
    });
  }

  function handleJobKeyDown(event, job) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openEditJob(job);
    }
  }

  async function changeJobLifecycle(job, action) {
    try {
      await apiFetch(`/jobs/${action}/${job.id}`, { method: "POST" });
      setPendingLifecycleAction(null);
      await load();
    } catch (err) {
      setToast(err.message);
    }
  }

  function confirmLifecycleChange(job, action) {
    setPendingLifecycleAction({ job, action });
  }

  function lifecycleConfirmCopy(action) {
    if (action === "pause") {
      return {
        header: "Pause Repeated Job",
        message: "Pause this repeated job? Future scheduled visits will be hidden until it is resumed.",
        confirmText: "Pause",
      };
    }

    return {
      header: "Resume Repeated Job",
      message: "Resume this repeated job? Future scheduled visits will appear again.",
      confirmText: "Resume",
    };
  }

  async function deleteJob() {
    if (!jobToDelete) {
      return;
    }

    try {
      await apiFetch(`/jobs/${jobToDelete.id}`, { method: "DELETE" });
      setJobToDelete(null);
      await load();
      setToast("Job deleted.");
    } catch (err) {
      setToast(err.message);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Jobs</IonTitle>

          <IonButtons slot="end">
            <IonButton onClick={openCreateJob}>
              <IonIcon icon={add} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <main className="page-pad">
          <section className="toolbar-panel">
            <IonInput
              className="metora-input compact-input"
              placeholder="Search jobs, clients, employees..."
              value={search}
              onIonInput={(e) => setSearch(e.detail.value ?? "")}
            />

            <SearchSelect
              placeholder="Status"
              value={status}
              onChange={setStatus}
              options={[
                { value: "", label: "All" },
                { value: "Scheduled", label: "Scheduled" },
                { value: "InProgress", label: "In Progress" },
                { value: "Complete", label: "Complete" },
                { value: "Cancelled", label: "Cancelled" },
              ]}
            />
          </section>

          <DateRangeNavigator value={dateFilters} onChange={setDateFilters} />

          <section className="list-stack">
            {jobs.map((job) => {
              const occurrence = primaryOccurrence(job);
              const seconds = trackedSecondsForOccurrence(job, occurrence);

              return (
                <article
                  className="panel row-card list-button"
                  key={job.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEditJob(job)}
                  onKeyDown={(event) => handleJobKeyDown(event, job)}
                >
                  <div>
                    <StatusBadge status={job.status} />
                    <h3>{jobLabel(job)}</h3>
                    <p>{job.serviceAddress}</p>
                    <p>
                      {formatDate(occurrence.scheduledStart)} · {formatTime(occurrence.scheduledStart)} -{" "}
                      {formatTime(occurrence.scheduledEnd)}
                    </p>
                    <p>{formatHoursMinutes(seconds)} tracked</p>
                    {job.repeatGroupId && <p>{repeatDetails(job)}</p>}
                  </div>

                  <div className="row-actions">
                    <IonButton
                      size="small"
                      color="danger"
                      fill="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        setJobToDelete(job);
                      }}
                    >
                      Delete
                    </IonButton>
                    {job.repeatGroupId && (
                      <>
                        <IonButton
                          size="small"
                          fill="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            confirmLifecycleChange(job, job.lifecycleStatus === "Paused" ? "resume" : "pause");
                          }}
                        >
                          {job.lifecycleStatus === "Paused" ? "Resume" : "Pause"}
                        </IonButton>
                        {job.lifecycleStatus !== "Ended" && (
                          <IonButton
                            size="small"
                            color="danger"
                            onClick={(event) => {
                              event.stopPropagation();
                              changeJobLifecycle(job, "end");
                            }}
                          >
                            End
                          </IonButton>
                        )}
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        </main>

        <IonModal isOpen={isCreateOpen} onDidDismiss={() => setIsCreateOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create Job</IonTitle>

              <IonButtons slot="end">
                <IonButton onClick={() => setIsCreateOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <SearchSelect
                  label="Client"
                  placeholder="Select client"
                  options={clients.map((client) => ({ value: client.id, label: clientLabel(client) }))}
                  value={form.clientId}
                  onChange={(clientId) => {
                    const selectedClient = clients.find((client) => client.id === clientId);

                    setForm({
                      ...form,
                      clientId,
                      serviceAddress: selectedClient?.address || "",
                    });
                  }}
                />

                <IonInput
                  className="metora-input"
                  label="Job Label"
                  labelPlacement="stacked"
                  placeholder="Example: Monday"
                  value={form.internalLabel}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      internalLabel: e.detail.value ?? "",
                    })
                  }
                />

                <AddressAutocompleteField
                  label="Service Address"
                  placeholder="Enter service address"
                  value={form.serviceAddress}
                  onChange={(serviceAddress) =>
                    setForm({
                      ...form,
                      serviceAddress,
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Scheduled Start"
                  labelPlacement="stacked"
                  type="datetime-local"
                  value={form.scheduledStart}
                  onIonInput={(e) => updateScheduledStart(e.detail.value ?? "", form, setForm)}
                />

                <div className="duration-inputs">
                  <IonInput
                    className="metora-input"
                    label="Hours"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    placeholder="0"
                    value={form.estimatedDurationHours}
                    onIonInput={(event) =>
                      setForm({
                        ...form,
                        estimatedDurationHours: event.detail.value ?? "",
                      })
                    }
                  />

                  <IonInput
                    className="metora-input"
                    label="Minutes"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    max="59"
                    placeholder="00"
                    value={form.estimatedDurationMinutesPart}
                    onIonInput={(event) =>
                      setForm({
                        ...form,
                        estimatedDurationMinutesPart: event.detail.value ?? "",
                      })
                    }
                    onIonBlur={() =>
                      setForm((current) => ({
                        ...current,
                        estimatedDurationMinutesPart: normalizeMinutePart(current.estimatedDurationMinutesPart),
                      }))
                    }
                  />
                </div>

                <SearchSelect
                  label="Assigned Employees"
                  multiple
                  placeholder="Select employees"
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.id === session?.user?.id ? "Myself (Admin)" : user.name,
                  }))}
                  value={form.assignedUserIds}
                  onChange={(assignedUserIds) =>
                    setForm({
                      ...form,
                      assignedUserIds,
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Service Notes"
                  labelPlacement="stacked"
                  placeholder="Enter service notes"
                  value={form.serviceNotes}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      serviceNotes: e.detail.value ?? "",
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Estimated Duration Minutes"
                  labelPlacement="stacked"
                  type="number"
                  readonly
                  value={estimatedDurationMinutes || ""}
                  placeholder="Calculated from estimated hours"
                />

                <label className="checkbox-row">
                  <IonCheckbox
                    checked={form.isRepeated}
                    onIonChange={(event) =>
                      setForm({
                        ...form,
                        isRepeated: event.detail.checked,
                        repeatIntervalWeeks: event.detail.checked ? form.repeatIntervalWeeks || 1 : 1,
                      })
                    }
                  />
                  <span>Is job repeated</span>
                </label>

                {form.isRepeated && (
                  <>
                    <IonInput
                      className="metora-input"
                      label="Interval in Weeks"
                      labelPlacement="stacked"
                      type="number"
                      min={1}
                      max={52}
                      value={form.repeatIntervalWeeks}
                      onIonInput={(event) =>
                        setForm({
                          ...form,
                          repeatIntervalWeeks: event.detail.value ?? "",
                        })
                      }
                    />
                  </>
                )}

                <FormNotice tone="warning">{createFormNotice}</FormNotice>

                <IonButton
                  expand="block"
                  onClick={createJob}
                  disabled={!form.scheduledStart || !estimatedDurationMinutes || Boolean(repeatError) || isCreatingJob}
                >
                  {isCreatingJob ? (
                    <>
                      <IonSpinner name="crescent" className="button-spinner" />
                      Creating Job
                    </>
                  ) : (
                    "Create Job"
                  )}
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(selectedJob)} onDidDismiss={() => setSelectedJob(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Edit Job</IonTitle>

              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedJob(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <SearchSelect
                  label="Client"
                  placeholder="Select client"
                  options={clients.map((client) => ({ value: client.id, label: clientLabel(client) }))}
                  value={editForm.clientId}
                  onChange={(clientId) => {
                    const selectedClient = clients.find((client) => client.id === clientId);

                    setEditForm({
                      ...editForm,
                      clientId,
                      serviceAddress: selectedClient?.address || editForm.serviceAddress,
                    });
                  }}
                />

                <IonInput
                  className="metora-input"
                  label="Job Label"
                  labelPlacement="stacked"
                  placeholder="Example: Monday"
                  value={editForm.internalLabel}
                  onIonInput={(e) =>
                    setEditForm({
                      ...editForm,
                      internalLabel: e.detail.value ?? "",
                    })
                  }
                />

                <AddressAutocompleteField
                  label="Service Address"
                  placeholder="Enter service address"
                  value={editForm.serviceAddress}
                  onChange={(serviceAddress) =>
                    setEditForm({
                      ...editForm,
                      serviceAddress,
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Scheduled Start"
                  labelPlacement="stacked"
                  type="datetime-local"
                  value={editForm.scheduledStart}
                  onIonInput={(e) => updateScheduledStart(e.detail.value ?? "", editForm, setEditForm)}
                />

                <div className="duration-inputs">
                  <IonInput
                    className="metora-input"
                    label="Hours"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    placeholder="0"
                    value={editForm.estimatedDurationHours}
                    onIonInput={(event) =>
                      setEditForm({
                        ...editForm,
                        estimatedDurationHours: event.detail.value ?? "",
                      })
                    }
                  />

                  <IonInput
                    className="metora-input"
                    label="Minutes"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    max="59"
                    placeholder="00"
                    value={editForm.estimatedDurationMinutesPart}
                    onIonInput={(event) =>
                      setEditForm({
                        ...editForm,
                        estimatedDurationMinutesPart: event.detail.value ?? "",
                      })
                    }
                    onIonBlur={() =>
                      setEditForm((current) => ({
                        ...current,
                        estimatedDurationMinutesPart: normalizeMinutePart(current.estimatedDurationMinutesPart),
                      }))
                    }
                  />
                </div>

                <SearchSelect
                  label="Assigned Employees"
                  multiple
                  placeholder="Select employees"
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.id === session?.user?.id ? "Myself (Admin)" : user.name,
                  }))}
                  value={editForm.assignedUserIds}
                  onChange={(assignedUserIds) =>
                    setEditForm({
                      ...editForm,
                      assignedUserIds,
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Service Notes"
                  labelPlacement="stacked"
                  placeholder="Enter service notes"
                  value={editForm.serviceNotes}
                  onIonInput={(e) =>
                    setEditForm({
                      ...editForm,
                      serviceNotes: e.detail.value ?? "",
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Estimated Duration Minutes"
                  labelPlacement="stacked"
                  type="number"
                  readonly
                  value={editEstimatedDurationMinutes || ""}
                  placeholder="Calculated from estimated hours"
                />

                <label className="checkbox-row">
                  <IonCheckbox
                    checked={editForm.isRepeated}
                    onIonChange={(event) =>
                      setEditForm({
                        ...editForm,
                        isRepeated: event.detail.checked,
                        repeatIntervalWeeks: event.detail.checked ? editForm.repeatIntervalWeeks || 1 : 1,
                      })
                    }
                  />
                  <span>Is job repeated</span>
                </label>

                {editForm.isRepeated && (
                  <>
                    <IonInput
                      className="metora-input"
                      label="Interval in Weeks"
                      labelPlacement="stacked"
                      type="number"
                      min={1}
                      max={52}
                      value={editForm.repeatIntervalWeeks}
                      onIonInput={(event) =>
                        setEditForm({
                          ...editForm,
                          repeatIntervalWeeks: event.detail.value ?? "",
                        })
                      }
                    />
                  </>
                )}

                <FormNotice tone="warning">{editFormNotice}</FormNotice>

                <IonButton
                  expand="block"
                  onClick={updateJob}
                  disabled={
                    !editForm.scheduledStart
                    || !editEstimatedDurationMinutes
                    || Boolean(editRepeatError)
                    || isUpdatingJob
                  }
                >
                  {isUpdatingJob ? (
                    <>
                      <IonSpinner name="crescent" className="button-spinner" />
                      Saving Job
                    </>
                  ) : (
                    "Save Job"
                  )}
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonAlert
          cssClass="metora-alert"
          isOpen={Boolean(jobToDelete)}
          header="Delete Job"
          message="This permanently deletes this job, its scheduled occurrences, assignments, and time entries. Jobs linked to invoices cannot be deleted."
          buttons={[
            {
              text: "Keep",
              role: "cancel",
            },
            {
              text: "Delete Job",
              role: "destructive",
              handler: deleteJob,
            },
          ]}
          onDidDismiss={() => setJobToDelete(null)}
        />

        <IonAlert
          cssClass="metora-alert"
          isOpen={Boolean(pendingLifecycleAction)}
          header={lifecycleConfirmCopy(pendingLifecycleAction?.action).header}
          message={lifecycleConfirmCopy(pendingLifecycleAction?.action).message}
          buttons={[
            {
              text: "Keep",
              role: "cancel",
            },
            {
              text: lifecycleConfirmCopy(pendingLifecycleAction?.action).confirmText,
              handler: () => changeJobLifecycle(pendingLifecycleAction.job, pendingLifecycleAction.action),
            },
          ]}
          onDidDismiss={() => setPendingLifecycleAction(null)}
        />

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
